import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { toast } from '@/components/feedback/useToast';
import { useResetMedicalStaffPassword } from '@/features/medical-staff/hooks/use-medical-staff-mutations';
import { MedicalStaffApiError } from '@/features/medical-staff/types/medical-staff.types';
import { EmailPreview } from '@/components/email/EmailPreview';

// Two entry modes (PRD 03 §3.7 + PRD 15 §3.6 / customer decisions log 2026-05-22 §6):
//   ?send=true&id=&name=&email=  — send mode (edit-panel button): action bar with
//                                  Cancel + Send shown above the email card.
//   (no ?send=true)              — preview-only mode: just back link above the email.
//
// The email body is the BE single-owner render (GET /admin/email-previews/staff-password-reset,
// from messages_ko). The Send button calls POST /admin/medical-staff/{id}/reset-password
// (mock until VITE_USE_MOCK_API=false).
//
// Canonical URL: `#/staff-password-reset-email` (matches prototype institution-managers.html L706).

export function StaffPasswordResetEmailPage() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetMutation = useResetMedicalStaffPassword();

  const sendMode = searchParams.get('send') === 'true';
  const idParam = searchParams.get('id');
  const id = idParam ? Number(idParam) : NaN;
  const nameParam = searchParams.get('name');
  const emailParam = searchParams.get('email');
  const name =
    nameParam || t('medicalStaffManagement.passwordResetEmailPreview.fallbackName');
  const email =
    emailParam ??
    t('medicalStaffManagement.passwordResetEmailPreview.fallbackRecipient');

  // SHCT-651 — these feed the server render's ONE meta box (`_layout.html :: page`); this page no
  // longer paints its own copy above it, which is what showed the header twice.
  //
  // SHCT-684 — `recipientEmail` carries the BARE address, never `name <email>`. The 받는사람 row is
  // composed by the message itself from name and address as two separate arguments, so a
  // pre-composed value was wrapped a second time and rendered `name <name <email>>`. Every live
  // send path passes the bare address, and the preview must match it. With no ?email= the slot is
  // left empty rather than repeating the name, which would read as an address.
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const sentAt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const recipient = nameParam
    ? emailParam ?? ''
    : t('medicalStaffManagement.passwordResetEmailPreview.metaRecipientFallback');

  const returnToList = () => navigate('/medical-staff-management');

  const handleSend = async () => {
    if (!Number.isFinite(id) || resetMutation.isPending) return;
    try {
      await resetMutation.mutateAsync(id);
      toast.success(
        t('medicalStaffManagement.toasts.resetPasswordSuccess', { email }),
      );
      returnToList();
    } catch (e) {
      const message =
        e instanceof MedicalStaffApiError
          ? e.message
          : t('medicalStaffManagement.toasts.resetPasswordError');
      toast.error(message);
    }
  };

  return (
    <div className="admin-email-preview-page">
      {!sendMode && (
        <div className="admin-email-preview-page__back">
          <Link to="/medical-staff-management">
            {t('medicalStaffManagement.passwordResetEmailPreview.backToList')}
          </Link>
        </div>
      )}

      {sendMode && (
        // Card-style send action bar (prototype .pwreset-send-bar), sized to match the email card.
        <div className="staff-pwreset-send-bar">
          <div
            className="staff-pwreset-send-bar__info"
            dangerouslySetInnerHTML={{
              __html: t(
                'medicalStaffManagement.passwordResetEmailPreview.actionBarInfo',
                { name },
              ),
            }}
          />
          <div className="staff-pwreset-send-bar__actions">
            <button
              type="button"
              onClick={returnToList}
              disabled={resetMutation.isPending}
              className="staff-pwreset-send-bar__cancel"
            >
              {t('medicalStaffManagement.actions.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={resetMutation.isPending || !Number.isFinite(id)}
              className="staff-pwreset-send-bar__send"
            >
              {resetMutation.isPending
                ? t('medicalStaffManagement.passwordResetEmailPreview.sending')
                : t('medicalStaffManagement.passwordResetEmailPreview.send')}
            </button>
          </div>
        </div>
      )}

      <EmailPreview
        kind="staff-password-reset"
        vars={{ name: nameParam ?? undefined, recipientEmail: recipient, sentAt }}
      />
    </div>
  );
}
